import {getSettingsHeaders} from "@next/utils"
import {IncomingHttpHeaders} from "http"
import {Action, Store} from "@reduxjs/toolkit"
import logger from "@next/core-logger"
import {SettingsSdkKeys} from "../../models/settings"
import {BackToSite} from "../../models/backToSite"
import {QUERY_PARAMETER_TIME_MACHINE_DATE, COUNTRY_GB, TERRITORY_HEADER} from "../../config/constants"
import {removeTpLitePathFromSiteUrl} from "../../utils/removeTpLitePath"
import env from "../../config/env"

export const SET_REQUEST = "SET_REQUEST"
export const SET_COOKIE_DOMAIN = "SET_COOKIE_DOMAIN"
export const SET_GEOLOCATION = "SET_GEOLOCATION"
export const SET_COOKIE_INFO = "SET_COOKIE_INFO"
export const SET_TABBED_NAV_COOKIE_INFO = "SET_TABBED_NAV_COOKIE_INFO"
export const SET_USE_TIME_MACHINE_COOKIE = "SET_USE_TIME_MACHINE_COOKIE"
export const SET_TERRITORY_DETAILS = "SET_TERRITORY_DETAILS"
export const SET_BLOOMREACH_INFO = "SET_BLOMREACH_INFO"
export const SET_MONETATE_INFO = "SET_MONETATE_INFO"
export const SET_SALE_WARNING_MODAL = "SET_SALE_WARNING_MODAL"
export const SET_PRIVACY_LINK = "SET_PRIVACY_LINK"
export const SET_SHOW_BACK_TO_SITE = "SET_SHOW_BACK_TO_SITE"
export const SET_BACK_TO_SITE = "SET_BACK_TO_SITE"
export const SET_TP_LITE_PATH = "SET_TP_LITE_PATH"

interface BloomreachData {
    bloomreachGroupLocation: string
    bloomreachDomainKey: string
    bloomreachViewId: string | null
}

interface MonetateData {
    monetateEnabled: boolean
}

interface TerritoryDetails {
    currencyCode: string
    fullTerritoryName: string
}

type BloomreachConf = {
    [SettingsSdkKeys.bloomreachGroupLocation]: any
}

type BackToSiteConf = {
    [SettingsSdkKeys.backToSite]: any
}

type TerritoryDetailsConf = {
    [SettingsSdkKeys.currencyCode]: any
    [SettingsSdkKeys.fullTerritoryName]: any
}

type ShowBannerConf = {
    [SettingsSdkKeys.showBackToSiteBanner]: {
        Value: boolean
    }
}
interface SetRequest {
    type: typeof SET_REQUEST
    payload: any
}

interface SetCookieDomain {
    type: typeof SET_COOKIE_DOMAIN
    payload: string
}

interface GeolocationData {
    url: string
    version: number
}
interface SetGeolocationData {
    type: typeof SET_GEOLOCATION
    payload: GeolocationData
}

interface SetUseTimeMachineCookie {
    type: typeof SET_USE_TIME_MACHINE_COOKIE
    payload: boolean
}

interface CookieData {
    bloomReachCachingCookieList: string
    bloomReachCachingEnabled: boolean
}
interface SetBrCookieInfo {
    type: typeof SET_COOKIE_INFO
    payload: CookieData
}

interface SetTabbedNavCookieInfo {
    type: typeof SET_TABBED_NAV_COOKIE_INFO
    payload: string
}

interface SetBloomreachInfo {
    type: typeof SET_BLOOMREACH_INFO
    payload: BloomreachData
}

interface SetTerritoryDetails {
    type: typeof SET_TERRITORY_DETAILS
    payload: TerritoryDetails
}

interface SetMonetateInfo {
    type: typeof SET_MONETATE_INFO
    payload: MonetateData
}
interface SetSaleWarningBag {
    type: typeof SET_SALE_WARNING_MODAL
    payload: boolean
}

interface SetPrivacyUrl {
    type: typeof SET_PRIVACY_LINK
    payload: string
}

interface SetShowBackToSite {
    type: typeof SET_SHOW_BACK_TO_SITE
    payload: boolean
}

interface SetBackToSite {
    type: typeof SET_BACK_TO_SITE
    payload: BackToSite
}

interface SetTpLitePath {
    type: typeof SET_TP_LITE_PATH
    payload: string
}

type Actions =
    | SetRequest
    | SetGeolocationData
    | SetBrCookieInfo
    | SetUseTimeMachineCookie
    | SetTerritoryDetails
    | SetBloomreachInfo
    | SetMonetateInfo
    | SetCookieDomain
    | SetSaleWarningBag
    | SetPrivacyUrl
    | SetShowBackToSite
    | SetBackToSite
    | SetTabbedNavCookieInfo
    | SetTpLitePath

export interface RequestDuckState {
    headers: IncomingHttpHeaders | null
    siteUrl: string
    url: string | null
    timeMachineDate: string | null
    isInternationalCountry: boolean
    geolocationBaseUrl: string
    geolocationVersion: number
    bloomReachCachingCookieList: string
    bloomReachCachingEnabled: boolean
    bloomreachGroupLocation: string
    bloomreachViewId: string | null
    bloomreachDomainKey: string
    useTimeMachineCookie: boolean
    currencyCode: string
    fullTerritoryName: string
    monetateSDK: boolean
    accountMonetateSDK: string
    cookieDomain: string
    showSaleWarningBag: boolean
    privacyUrl: string
    showBackToSiteBanner: boolean
    backToSite: BackToSite
    tabbedNavCookie: string
    cleanSiteUrl: string
    tpLitePath: string
}

const initialState: RequestDuckState = {
    headers: null,
    siteUrl: "",
    url: null,
    timeMachineDate: null,
    isInternationalCountry: false,
    geolocationBaseUrl: "",
    geolocationVersion: 0,
    bloomReachCachingCookieList: "",
    bloomReachCachingEnabled: false,
    bloomreachGroupLocation: "",
    bloomreachViewId: null,
    bloomreachDomainKey: "",
    useTimeMachineCookie: false,
    currencyCode: "",
    fullTerritoryName: "",
    monetateSDK: false,
    accountMonetateSDK: "",
    cookieDomain: "",
    showSaleWarningBag: false,
    privacyUrl: "",
    showBackToSiteBanner: false,
    backToSite: {} as BackToSite,
    tabbedNavCookie: "",
    cleanSiteUrl: "",
    tpLitePath: "",
}

export const setRequestAction = (store: Store, request: any) => {
    store.dispatch({
        type: SET_REQUEST,
        payload: request,
    })
}

export const setCokieDomainAction = (store: Store, cookieDomain: string) => {
    store.dispatch({
        type: SET_COOKIE_DOMAIN,
        payload: cookieDomain,
    })
}

export const setSaleWarningModalAction = (store: Store, enableSaleWarningModal: boolean) => {
    store.dispatch({
        type: SET_SALE_WARNING_MODAL,
        payload: enableSaleWarningModal,
    })
}

const setUseTimeMachineCookie = (payload: boolean): Actions => ({
    type: SET_USE_TIME_MACHINE_COOKIE,
    payload,
})
const setGeolocationUrl = (payload: GeolocationData): Actions => ({
    type: SET_GEOLOCATION,
    payload,
})

const setBrCookieInfo = (payload: CookieData): Actions => ({
    type: SET_COOKIE_INFO,
    payload,
})

const setTabbedNavCookieInfo = (payload: string): Actions => ({
    type: SET_TABBED_NAV_COOKIE_INFO,
    payload,
})

const setBloomreachInfo = (payload: BloomreachData): Actions => ({
    type: SET_BLOOMREACH_INFO,
    payload,
})

const setTerritoryDetails = (payload: TerritoryDetails): Actions => ({
    type: SET_TERRITORY_DETAILS,
    payload,
})

const setMonetateInfo = (payload: MonetateData): Actions => ({
    type: SET_MONETATE_INFO,
    payload,
})

const setPrivacyUrl = (payload: string): Actions => ({
    type: SET_PRIVACY_LINK,
    payload,
})

const setShowBackToSite = (payload: boolean): Actions => ({
    type: SET_SHOW_BACK_TO_SITE,
    payload,
})

const setBackToSite = (payload: BackToSite): Actions => ({
    type: SET_BACK_TO_SITE,
    payload,
})

const setTpLitePath = (payload: string): Actions => ({
    type: SET_TP_LITE_PATH,
    payload,
})

export const setTerritoryDetailsAction = (store: Store, configuration: TerritoryDetailsConf) => {
    const currencyCode = configuration[SettingsSdkKeys.currencyCode]?.Value
    const fullTerritoryName = configuration[SettingsSdkKeys.fullTerritoryName]?.Value
    store.dispatch<Action>(setTerritoryDetails({currencyCode, fullTerritoryName}))
}

export const setMonetateInfoAction = (store: Store, monetateEnabled: boolean) => {
    store.dispatch<Action>(setMonetateInfo({monetateEnabled}))
}

export const setBackToSiteAction = (store: Store, configuration: BackToSiteConf) => {
    const backToSite = configuration[SettingsSdkKeys.backToSite]
    store.dispatch<Action>(setBackToSite(backToSite))
}

export const setBrCookieInfoAction = (
    store: Store,
    bloomReachCachingEnabled: boolean,
    bloomReachCachingCookieList: string,
) => {
    store.dispatch<Action>(setBrCookieInfo({bloomReachCachingCookieList, bloomReachCachingEnabled}))
}

export const setTabbedNavCookieInfoAction = (store: Store, cookieValue: string) => {
    store.dispatch<Action>(setTabbedNavCookieInfo(cookieValue))
}

export const setBloomreachInfoAction = (store: Store, configuration: BloomreachConf, bloomreachDomainKey: string) => {
    const bloomreachGroupLocation = configuration[SettingsSdkKeys.bloomreachGroupLocation]?.Value
    const bloomreachViewId = configuration[SettingsSdkKeys.bloomreachViewId]?.Value
    store.dispatch<Action>(setBloomreachInfo({bloomreachGroupLocation, bloomreachDomainKey, bloomreachViewId}))
}

const getGeolocationVersion = (configuration: any) => configuration[SettingsSdkKeys.geolocationVersion]?.Value

export const setGeolocationUrlAction = (store: Store, url: string, configuration: any) => {
    const version = getGeolocationVersion(configuration)
    store.dispatch<Action>(setGeolocationUrl({url, version}))
}
export const setUseTimeMachineCookieAction = (store: Store, useTimeMachineCookie: string) => {
    const acceptableValues = ["true", "false"]
    const lowerCaseUseTMCookie = useTimeMachineCookie.toLowerCase()
    if (acceptableValues.includes(lowerCaseUseTMCookie)) {
        const useTimeMachineCookieValue = lowerCaseUseTMCookie === "true"
        store.dispatch<Action>(setUseTimeMachineCookie(useTimeMachineCookieValue))
    } else {
        logger.warn(
            `Warning: invalid value set for use time machine cookie: ${useTimeMachineCookie} should be either 'true' or 'false'. useTimeMachineCookie is set to false`,
        )
    }
}

export const setPrivacyUrlAction = (store: Store, privacyUrl: string) => {
    store.dispatch<Action>(setPrivacyUrl(privacyUrl))
}

export const setShowBackToSiteBannerAction = (store: Store, conf: ShowBannerConf) => {
    const showBanner = conf[SettingsSdkKeys.showBackToSiteBanner].Value
    if (showBanner) store.dispatch<Action>(setShowBackToSite(true))
}

export const closeBannerAction = () => (_dispatch: any) => {
    _dispatch(setShowBackToSite(false))
}

export const setTpLitePathAction = (store: Store, tpLitePath: string) => {
    store.dispatch<Action>(setTpLitePath(tpLitePath))
}

const reducer = (state: RequestDuckState = initialState, action: Actions): RequestDuckState => {
    switch (action.type) {
        case SET_REQUEST:
            return {
                ...state,
                headers: getSettingsHeaders(action.payload.headers) as IncomingHttpHeaders,
                siteUrl: `${action.payload.siteUrl.url}${env.REACT_APP_SERVE_PATH_PREFIX}`,
                url: action.payload.url,
                timeMachineDate: action.payload.query[QUERY_PARAMETER_TIME_MACHINE_DATE],
                isInternationalCountry:
                    action.payload.headers[TERRITORY_HEADER] &&
                    action.payload.headers[TERRITORY_HEADER].toLowerCase() !== COUNTRY_GB,
                cleanSiteUrl: removeTpLitePathFromSiteUrl(
                    `${action.payload.siteUrl.url}${env.REACT_APP_SERVE_PATH_PREFIX}`,
                    state.tpLitePath,
                ),
            }
        case SET_COOKIE_DOMAIN:
            return {...state, cookieDomain: action.payload}
        case SET_GEOLOCATION:
            return {...state, geolocationBaseUrl: action.payload.url, geolocationVersion: action.payload.version}
        case SET_USE_TIME_MACHINE_COOKIE:
            return {...state, useTimeMachineCookie: action.payload}
        case SET_COOKIE_INFO:
            return {
                ...state,
                bloomReachCachingCookieList: action.payload.bloomReachCachingCookieList,
                bloomReachCachingEnabled: action.payload.bloomReachCachingEnabled,
            }
        case SET_BLOOMREACH_INFO:
            return {
                ...state,
                bloomreachGroupLocation: action.payload.bloomreachGroupLocation,
                bloomreachDomainKey: action.payload.bloomreachDomainKey,
                bloomreachViewId: action.payload.bloomreachViewId,
            }
        case SET_TERRITORY_DETAILS:
            return {
                ...state,
                currencyCode: action.payload.currencyCode,
                fullTerritoryName: action.payload.fullTerritoryName,
            }
        case SET_MONETATE_INFO:
            return {...state, monetateSDK: action.payload.monetateEnabled}
        case SET_SALE_WARNING_MODAL:
            return {...state, showSaleWarningBag: action.payload}
        case SET_PRIVACY_LINK:
            return {...state, privacyUrl: action.payload}
        case SET_SHOW_BACK_TO_SITE:
            return {...state, showBackToSiteBanner: action.payload}
        case SET_BACK_TO_SITE:
            return {...state, backToSite: action.payload}
        case SET_TABBED_NAV_COOKIE_INFO:
            return {...state, tabbedNavCookie: action.payload}
        case SET_TP_LITE_PATH:
            return {...state, tpLitePath: action.payload}
        default:
            return state
    }
}

export default reducer
